import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Plus, Search, Lightbulb, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, LoadingState } from "@/components/states";
import { usePermissions } from "@/hooks/usePermissions";
import { useCollectionPrefs } from "@/hooks/useCollectionPrefs";
import { CollectionControls } from "@/components/collections/CollectionControls";
import { SortMenu } from "@/components/collections/SortMenu";
import { FilterChips } from "@/components/collections/FilterChips";
import { TopicView } from "@/components/topics/TopicView";
import { TopicDrawer } from "@/components/topics/TopicDrawer";
import { TopicCard } from "@/components/topics/TopicCard";
import { TopicFilters } from "@/components/topics/TopicFilters";
import { topicCollectionDef, TOPIC_SORTS, ASPECT_LABEL, DATE_PRESET_LABEL, presetRange, STATUS_META } from "@/components/topics/topicCollection";

const PAGE = 25;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const DEFAULT_FILTERS = {
  priority: "", owner_id: "", has_inspiration: "", platform: "", aspect: "",
  topic_status: "", needs_my_review: false,
  updatedPreset: "any", updatedFrom: "", updatedTo: "",
  createdPreset: "any", createdFrom: "", createdTo: "",
};

export default function Topics() {
  const perms = usePermissions();
  const canCreate = perms.has("create_topics");
  const canArchive = perms.has("archive_topic");
  const canEditAll = perms.is_owner || perms.is_admin || perms.has("edit_all_topics");
  const { cfg, update: updateCfg } = useCollectionPrefs("topics", topicCollectionDef);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("active");
  const [scope, setScope] = useState("all");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [members, setMembers] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [editor, setEditor] = useState(null);
  const debounce = useRef(null);
  const sort = cfg.sort || "updated";

  useEffect(() => { apiFetch("/members").then((m) => setMembers((m || []).filter((x) => x.status === "Active"))).catch(() => {}); }, []);

  const buildParams = useCallback((offset) => {
    const p = new URLSearchParams({ status: statusF, scope, sort, limit: String(PAGE), offset: String(offset) });
    if (q.trim()) p.set("q", q.trim());
    ["priority", "owner_id", "has_inspiration", "platform", "aspect", "topic_status"].forEach((k) => filters[k] && p.set(k, filters[k]));
    if (filters.needs_my_review) p.set("needs_my_review", "true");
    const u = presetRange(filters.updatedPreset, filters.updatedFrom, filters.updatedTo);
    if (u.from) p.set("updated_from", u.from); if (u.to) p.set("updated_to", u.to);
    const c = presetRange(filters.createdPreset, filters.createdFrom, filters.createdTo);
    if (c.from) p.set("created_from", c.from); if (c.to) p.set("created_to", c.to);
    return p.toString();
  }, [statusF, scope, sort, q, filters]);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/topics?${buildParams(0)}`)
      .then((r) => { setItems(r.items || []); setTotal(r.total || 0); })
      .catch((e) => toast.error("Could not load topics", { description: e.message }))
      .finally(() => setLoading(false));
  }, [buildParams]);

  const paramsKey = useMemo(() => buildParams(0), [buildParams]);
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(debounce.current);
  }, [paramsKey]); // eslint-disable-line

  const loadMore = () => {
    setLoadingMore(true);
    apiFetch(`/topics?${buildParams(items.length)}`)
      .then((r) => { setItems((prev) => [...prev, ...(r.items || [])]); setTotal(r.total || 0); })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoadingMore(false));
  };

  const patchFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const ownerName = (id) => members.find((m) => m.id === id)?.full_name || members.find((m) => m.id === id)?.login_email || "Owner";

  const chips = [];
  if (filters.priority) chips.push({ key: "priority", label: `Priority: ${cap(filters.priority)}`, onRemove: () => patchFilter({ priority: "" }) });
  if (filters.owner_id) chips.push({ key: "owner", label: `Owner: ${ownerName(filters.owner_id)}`, onRemove: () => patchFilter({ owner_id: "" }) });
  if (filters.has_inspiration) chips.push({ key: "has", label: filters.has_inspiration === "yes" ? "Has inspiration" : "No inspiration", onRemove: () => patchFilter({ has_inspiration: "" }) });
  if (filters.platform) chips.push({ key: "platform", label: `Platform: ${filters.platform}`, onRemove: () => patchFilter({ platform: "" }) });
  if (filters.aspect) chips.push({ key: "aspect", label: `Aspect: ${ASPECT_LABEL[filters.aspect]}`, onRemove: () => patchFilter({ aspect: "" }) });
  if (filters.topic_status) chips.push({ key: "topic_status", label: `Status: ${STATUS_META[filters.topic_status]?.label || filters.topic_status}`, onRemove: () => patchFilter({ topic_status: "" }) });
  if (filters.needs_my_review) chips.push({ key: "needs_review", label: "Needs my review", onRemove: () => patchFilter({ needs_my_review: false }) });
  if (filters.updatedPreset !== "any") chips.push({ key: "updated", label: `Updated: ${DATE_PRESET_LABEL[filters.updatedPreset]}`, onRemove: () => patchFilter({ updatedPreset: "any", updatedFrom: "", updatedTo: "" }) });
  if (filters.createdPreset !== "any") chips.push({ key: "created", label: `Created: ${DATE_PRESET_LABEL[filters.createdPreset]}`, onRemove: () => patchFilter({ createdPreset: "any", createdFrom: "", createdTo: "" }) });
  const activeCount = chips.length;
  const filtersActive = !!q || scope !== "all" || statusF !== "active" || activeCount > 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6" data-testid="topics-page">
      <PageHeader eyebrow="Content planning" title="Topics & Ideas"
        subtitle="Capture the subjects and ideas Nirvana wants to explore and develop into content."
        actions={canCreate && <Button size="sm" className="gap-1.5" onClick={() => setEditor({})} data-testid="topic-new"><Plus className="h-4 w-4" /> New Topic</Button>} />

      {/* toolbar */}
      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search topics & inspiration…" className="pl-9" data-testid="topic-search" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={scope} onValueChange={setScope}><SelectTrigger className="h-9 w-[120px]" data-testid="topic-filter-scope"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All topics</SelectItem><SelectItem value="mine">My topics</SelectItem></SelectContent></Select>
          <Select value={statusF} onValueChange={setStatusF}><SelectTrigger className="h-9 w-[120px]" data-testid="topic-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent></Select>
          <TopicFilters filters={filters} patch={patchFilter} members={members} activeCount={activeCount} />
          <SortMenu options={TOPIC_SORTS} value={sort} onChange={(v) => updateCfg({ sort: v })} />
          <CollectionControls def={topicCollectionDef} cfg={cfg} update={updateCfg} />
        </div>
      </div>

      {/* result count + active filter chips */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-muted-foreground" data-testid="topic-result-count">
          {loading ? "Loading…" : items.length < total ? `Showing ${items.length} of ${total} Topics` : `${total} ${total === 1 ? "Topic" : "Topics"}`}
        </p>
        <FilterChips chips={chips} onClearAll={() => setFilters(DEFAULT_FILTERS)} />
      </div>

      {loading ? <LoadingState label="Loading topics…" />
        : items.length === 0 ? (
          <EmptyState icon={Lightbulb} title={filtersActive ? "No topics match your filters" : "No topics yet"}
            message={filtersActive ? "Try adjusting your search or filters." : "Capture the ideas you want Nirvana to explore and develop into content."}
            action={canCreate && !filtersActive ? <Button size="sm" className="gap-1.5" onClick={() => setEditor({})} data-testid="topic-empty-new"><Plus className="h-4 w-4" /> Create Topic</Button> : null} />
        ) : (
          <>
            <div className="grid gap-2.5" data-testid="topic-list">
              {items.map((t) => <TopicCard key={t.id} t={t} cfg={cfg} onOpen={setViewer} />)}
            </div>
            {items.length < total && (
              <div className="mt-5 flex justify-center">
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="topic-load-more">
                  {loadingMore ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Loading…</> : `Load more (${total - items.length})`}
                </Button>
              </div>
            )}
          </>
        )}

      {viewer && <TopicView topicId={viewer} canArchive={canArchive} onClose={() => setViewer(null)}
        onEdit={() => { setEditor({ id: viewer }); setViewer(null); }} onChanged={load} />}
      {editor && <TopicDrawer topicId={editor.id} canEditAll={canEditAll}
        onClose={() => setEditor(null)} onSaved={(id) => { setEditor(null); load(); if (id) setViewer(id); }} />}
    </div>
  );
}
